import { useTranslation } from "react-i18next"

import type { Project } from "@/services/project-service"

import { Bell, Crown, UserPlus, X } from "@/components/icons"
import { useMarkProjectViewed } from "@/hooks/use-projects"

type ProjectUpdatesStripProps = {
  projects: Project[]
  onOpen: (project: Project) => void
}

/**
 * Dismissible "Updates" strip at the top of the Projects section. Surfaces new
 * shares and ownership transfers (projects flagged hasUnviewedNotification),
 * sharing the same unviewed state as the per-item dot — opening or dismissing
 * marks the notification viewed via the existing API. Mirrors ChatUpdatesStrip.
 */
export function ProjectUpdatesStrip({ projects, onOpen }: ProjectUpdatesStripProps) {
  const { t } = useTranslation()
  const markViewed = useMarkProjectViewed()

  const updates = projects.filter((proj) =>
    Boolean(proj.hasUnviewedNotification && proj.invitationNotification)
  )

  if (updates.length === 0) {
    return null
  }

  const dismissAll = () => {
    updates.forEach((proj) => {
      markViewed.mutate(proj.id)
    })
  }

  return (
    <div className="border-warning/25 bg-warning/10 mx-1 mb-2 rounded-md border p-1">
      <div className="flex items-center gap-1.5 px-1.5 pt-0.5 pb-1">
        <Bell className="text-warning-foreground size-3 shrink-0" />
        <span className="text-warning-foreground flex-1 text-xs font-semibold">
          {t("projects.updates", { defaultValue: "Updates" })} · {updates.length}
        </span>
        <button
          aria-label={t("projects.dismiss_updates", { defaultValue: "Dismiss updates" })}
          className="text-muted-foreground hover:text-foreground shrink-0 transition-colors"
          type="button"
          onClick={dismissAll}
        >
          <X className="size-3.5" />
        </button>
      </div>
      <div className="flex flex-col">
        {updates.map((proj) => {
          const isOwner = proj.invitationNotification?.role === "owner"
          const name =
            proj.invitationNotification?.invitedByDisplayName ||
            t("projects.someone", { defaultValue: "Someone" })
          return (
            <button
              key={proj.id}
              className="hover:bg-sidebar-accent/40 flex w-full items-center gap-2 rounded px-1.5 py-1 text-left transition-colors"
              type="button"
              onClick={() => {
                markViewed.mutate(proj.id)
                onOpen(proj)
              }}
            >
              {isOwner ? (
                <Crown className="text-warning size-3.5 shrink-0" />
              ) : (
                <UserPlus className="text-muted-foreground size-3.5 shrink-0" />
              )}
              <span className="text-sidebar-foreground min-w-0 flex-1 truncate text-xs">
                {isOwner
                  ? t("projects.update_made_owner", {
                      name,
                      defaultValue: "{{name}} made you owner",
                    })
                  : t("projects.update_shared", {
                      name,
                      defaultValue: "{{name}} shared a project",
                    })}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
